import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Skeleton } from "@app/components/shared/Skeleton";
import { SectionHeader } from "@app/components/shared/SectionHeader";
import {
  getTradingKillSwitches,
  updateTradingEnabled,
  updateEntryEnabled,
  updateExitEnabled,
  updateNewSignalsEnabled,
  updateMaxGlobalConcurrency,
  updatePositionMonitorCanSendOrders,
  type TradingKillSwitches,
} from "@app/services/adminSystemSettingsApi";
import { logger } from "@app/services/logger";
import { DailyCircuitBreakerCard } from "./DailyCircuitBreakerCard";
import "./adminTradingPipeline.css";

export function AdminTradingPipelineTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [updatingSwitch, setUpdatingSwitch] = useState<string | null>(null);

  const {
    data: killSwitches,
    isLoading,
    error,
  } = useQuery<TradingKillSwitches>({
    queryKey: ["adminTradingKillSwitches"],
    queryFn: getTradingKillSwitches,
  });

  const createUpdateMutation = (
    switchName: string,
    updateFn: (value: boolean | number) => Promise<TradingKillSwitches>,
  ) => {
    return useMutation({
      mutationFn: updateFn,
      onMutate: () => {
        setUpdatingSwitch(switchName);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["adminTradingKillSwitches"],
        });
        setUpdatingSwitch(null);
      },
      onError: (error: any) => {
        logger.error(`Failed to update ${switchName}`, error);
        alert(
          `Failed to update ${switchName}: ${error.response?.data?.detail || error.message}`,
        );
        setUpdatingSwitch(null);
      },
    });
  };

  const tradingEnabledMutation = createUpdateMutation(
    "TRADING_ENABLED",
    (enabled: boolean | number) => updateTradingEnabled(enabled as boolean),
  );

  const entryEnabledMutation = createUpdateMutation(
    "ENTRY_ENABLED",
    (enabled: boolean | number) => updateEntryEnabled(enabled as boolean),
  );

  const exitEnabledMutation = createUpdateMutation(
    "EXIT_ENABLED",
    (enabled: boolean | number) => updateExitEnabled(enabled as boolean),
  );

  const newSignalsEnabledMutation = createUpdateMutation(
    "NEW_SIGNALS_ENABLED",
    (enabled: boolean | number) => updateNewSignalsEnabled(enabled as boolean),
  );

  const maxConcurrencyMutation = createUpdateMutation(
    "MAX_GLOBAL_CONCURRENCY",
    (value: boolean | number) => updateMaxGlobalConcurrency(value as number),
  );

  const positionMonitorCanSendOrdersMutation = createUpdateMutation(
    "POSITION_MONITOR_CAN_SEND_ORDERS",
    (enabled: boolean | number) => updatePositionMonitorCanSendOrders(enabled as boolean),
  );

  const handleToggle = (
    switchName: string,
    currentValue: boolean,
    mutation: ReturnType<typeof createUpdateMutation>,
  ) => {
    const newValue = !currentValue;
    const confirmMessage = newValue
      ? `Enable ${switchName}? This will allow the system to ${switchName.toLowerCase().replace(/_/g, " ")}.`
      : `Disable ${switchName}? This will prevent the system from ${switchName.toLowerCase().replace(/_/g, " ")}.`;

    if (confirm(confirmMessage)) {
      mutation.mutate(newValue);
    }
  };

  const handleConcurrencyChange = () => {
    const currentValue = killSwitches?.MAX_GLOBAL_CONCURRENCY ?? 100;
    const newValue = prompt(
      `Enter new maximum global concurrency (current: ${currentValue}):`,
      currentValue.toString(),
    );

    if (newValue === null) return;

    const parsedValue = parseInt(newValue, 10);
    if (isNaN(parsedValue) || parsedValue < 1) {
      alert("Please enter a valid number greater than 0");
      return;
    }

    if (confirm(`Set maximum global concurrency to ${parsedValue}?`)) {
      maxConcurrencyMutation.mutate(parsedValue);
    }
  };

  if (isLoading) {
    return (
      <div className="admin-trading-pipeline-root">
        <SectionHeader
          title="Trading System Pipeline & Logic"
          subtitle="Control global trading settings and system-wide kill switches"
        />
        <Card className="admin-trading-pipeline-card">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Skeleton />
            <Skeleton />
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-trading-pipeline-root">
        <SectionHeader
          title="Trading System Pipeline & Logic"
          subtitle="Control global trading settings and system-wide kill switches"
        />
        <Card className="admin-trading-pipeline-card">
          <div className="admin-trading-pipeline-error">
            Error loading kill switches:{" "}
            {error instanceof Error ? error.message : "Unknown error"}
          </div>
        </Card>
      </div>
    );
  }

  const switches = killSwitches!;

  return (
    <div className="admin-trading-pipeline-root">
      <SectionHeader
        title="Trading Master Switches"
        subtitle="Enable/disable trading, entries, exits, and signals system-wide. These settings affect all users system-wide."
      />

      <Card className="admin-trading-pipeline-card">
        <div className="admin-trading-pipeline-warning">
          <strong>Warning:</strong> These are global system settings that affect
          all users. Use with caution.
        </div>

        <div className="admin-trading-pipeline-switches">
          <div className="admin-trading-pipeline-switch-item">
            <div className="admin-trading-pipeline-switch-info">
              <div className="admin-trading-pipeline-switch-header">
                <h4>Global Trading</h4>
                <span
                  className={`admin-trading-pipeline-status-badge ${
                    switches.TRADING_ENABLED
                      ? "admin-trading-pipeline-status-enabled"
                      : "admin-trading-pipeline-status-disabled"
                  }`}
                >
                  {switches.TRADING_ENABLED ? "Enabled" : "Disabled"}
                </span>
              </div>
              <p className="admin-trading-pipeline-switch-description">
                Master switch for all trading operations. When disabled, no
                trades will be executed for any user, regardless of individual
                settings.
              </p>
            </div>
            <Button
              type="button"
              variant={switches.TRADING_ENABLED ? "danger" : "primary"}
              onClick={() =>
                handleToggle(
                  "TRADING_ENABLED",
                  switches.TRADING_ENABLED,
                  tradingEnabledMutation,
                )
              }
              disabled={
                updatingSwitch === "TRADING_ENABLED" ||
                tradingEnabledMutation.isPending
              }
            >
              {updatingSwitch === "TRADING_ENABLED" ||
              tradingEnabledMutation.isPending
                ? "Updating..."
                : switches.TRADING_ENABLED
                  ? "Disable"
                  : "Enable"}
            </Button>
          </div>

          <div className="admin-trading-pipeline-subsection">
            <DailyCircuitBreakerCard />
          </div>

          <div className="admin-trading-pipeline-switch-item">
            <div className="admin-trading-pipeline-switch-info">
              <div className="admin-trading-pipeline-switch-header">
                <h4>Entry Trading</h4>
                <span
                  className={`admin-trading-pipeline-status-badge ${
                    switches.ENTRY_ENABLED
                      ? "admin-trading-pipeline-status-enabled"
                      : "admin-trading-pipeline-status-disabled"
                  }`}
                >
                  {switches.ENTRY_ENABLED ? "Enabled" : "Disabled"}
                </span>
              </div>
              <p className="admin-trading-pipeline-switch-description">
                Controls entry order execution. When disabled, no new positions
                will be opened, but exit orders may still execute.
              </p>
            </div>
            <Button
              type="button"
              variant={switches.ENTRY_ENABLED ? "danger" : "primary"}
              onClick={() =>
                handleToggle(
                  "ENTRY_ENABLED",
                  switches.ENTRY_ENABLED,
                  entryEnabledMutation,
                )
              }
              disabled={
                updatingSwitch === "ENTRY_ENABLED" ||
                entryEnabledMutation.isPending
              }
            >
              {updatingSwitch === "ENTRY_ENABLED" ||
              entryEnabledMutation.isPending
                ? "Updating..."
                : switches.ENTRY_ENABLED
                  ? "Disable"
                  : "Enable"}
            </Button>
          </div>

          <div className="admin-trading-pipeline-switch-item">
            <div className="admin-trading-pipeline-switch-info">
              <div className="admin-trading-pipeline-switch-header">
                <h4>Exit Trading</h4>
                <span
                  className={`admin-trading-pipeline-status-badge ${
                    switches.EXIT_ENABLED
                      ? "admin-trading-pipeline-status-enabled"
                      : "admin-trading-pipeline-status-disabled"
                  }`}
                >
                  {switches.EXIT_ENABLED ? "Enabled" : "Disabled"}
                </span>
              </div>
              <p className="admin-trading-pipeline-switch-description">
                Controls exit order execution. When disabled, positions will not
                be closed automatically, but entry orders may still execute.
              </p>
            </div>
            <Button
              type="button"
              variant={switches.EXIT_ENABLED ? "danger" : "primary"}
              onClick={() =>
                handleToggle(
                  "EXIT_ENABLED",
                  switches.EXIT_ENABLED,
                  exitEnabledMutation,
                )
              }
              disabled={
                updatingSwitch === "EXIT_ENABLED" ||
                exitEnabledMutation.isPending
              }
            >
              {updatingSwitch === "EXIT_ENABLED" ||
              exitEnabledMutation.isPending
                ? "Updating..."
                : switches.EXIT_ENABLED
                  ? "Disable"
                  : "Enable"}
            </Button>
          </div>

          <div className="admin-trading-pipeline-switch-item">
            <div className="admin-trading-pipeline-switch-info">
              <div className="admin-trading-pipeline-switch-header">
                <h4>New Signals</h4>
                <span
                  className={`admin-trading-pipeline-status-badge ${
                    switches.NEW_SIGNALS_ENABLED
                      ? "admin-trading-pipeline-status-enabled"
                      : "admin-trading-pipeline-status-disabled"
                  }`}
                >
                  {switches.NEW_SIGNALS_ENABLED ? "Enabled" : "Disabled"}
                </span>
              </div>
              <p className="admin-trading-pipeline-switch-description">
                Controls whether new trading signals are accepted and processed.
                When disabled, new signals will be ignored.
              </p>
            </div>
            <Button
              type="button"
              variant={switches.NEW_SIGNALS_ENABLED ? "danger" : "primary"}
              onClick={() =>
                handleToggle(
                  "NEW_SIGNALS_ENABLED",
                  switches.NEW_SIGNALS_ENABLED,
                  newSignalsEnabledMutation,
                )
              }
              disabled={
                updatingSwitch === "NEW_SIGNALS_ENABLED" ||
                newSignalsEnabledMutation.isPending
              }
            >
              {updatingSwitch === "NEW_SIGNALS_ENABLED" ||
              newSignalsEnabledMutation.isPending
                ? "Updating..."
                : switches.NEW_SIGNALS_ENABLED
                  ? "Disable"
                  : "Enable"}
            </Button>
          </div>

          <div className="admin-trading-pipeline-switch-item">
            <div className="admin-trading-pipeline-switch-info">
              <div className="admin-trading-pipeline-switch-header">
                <h4>Max Global Concurrency</h4>
                <span className="admin-trading-pipeline-status-badge admin-trading-pipeline-status-info">
                  {switches.MAX_GLOBAL_CONCURRENCY}
                </span>
              </div>
              <p className="admin-trading-pipeline-switch-description">
                Maximum number of concurrent trading operations allowed
                system-wide. This helps prevent system overload.
              </p>
            </div>
            <Button
              type="button"
              variant="primary"
              onClick={handleConcurrencyChange}
              disabled={
                updatingSwitch === "MAX_GLOBAL_CONCURRENCY" ||
                maxConcurrencyMutation.isPending
              }
            >
              {updatingSwitch === "MAX_GLOBAL_CONCURRENCY" ||
              maxConcurrencyMutation.isPending
                ? "Updating..."
                : "Change"}
            </Button>
          </div>

          <div className="admin-trading-pipeline-switch-item">
            <div className="admin-trading-pipeline-switch-info">
              <div className="admin-trading-pipeline-switch-header">
                <h4>Position Monitor Can Send Orders</h4>
                <span
                  className={`admin-trading-pipeline-status-badge ${
                    switches.POSITION_MONITOR_CAN_SEND_ORDERS
                      ? "admin-trading-pipeline-status-enabled"
                      : "admin-trading-pipeline-status-disabled"
                  }`}
                >
                  {switches.POSITION_MONITOR_CAN_SEND_ORDERS ? "Enabled" : "Disabled"}
                </span>
              </div>
              <p className="admin-trading-pipeline-switch-description">
                Controls whether the position monitoring service can send orders directly.
                When enabled, the position monitor can create and send exit orders.
                When disabled, only the signal dispatcher can send orders.
              </p>
            </div>
            <Button
              type="button"
              variant={switches.POSITION_MONITOR_CAN_SEND_ORDERS ? "danger" : "primary"}
              onClick={() =>
                handleToggle(
                  "POSITION_MONITOR_CAN_SEND_ORDERS",
                  switches.POSITION_MONITOR_CAN_SEND_ORDERS,
                  positionMonitorCanSendOrdersMutation,
                )
              }
              disabled={
                updatingSwitch === "POSITION_MONITOR_CAN_SEND_ORDERS" ||
                positionMonitorCanSendOrdersMutation.isPending
              }
            >
              {updatingSwitch === "POSITION_MONITOR_CAN_SEND_ORDERS" ||
              positionMonitorCanSendOrdersMutation.isPending
                ? "Updating..."
                : switches.POSITION_MONITOR_CAN_SEND_ORDERS
                  ? "Disable"
                  : "Enable"}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

